import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { AccountInfo } from '@azure/msal-browser';
import { catchError, from, switchMap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { isApimKeyRejection } from '../apim/apim-subscription';
import { AuthService } from './auth.service';
import { MerchantService } from './merchant.service';

/** Every API the signed-in customer's token is for: Klidi.Api, BC Identity and BC Corporate. */
const TOKEN_ORIGINS = [
  environment.apiBaseUrl,
  environment.apiConfig.identityAPI.endpoint,
  environment.apiConfig.corporateAPI.endpoint
].filter(Boolean);

/**
 * Attaches the merchant's External ID token (the ID token, see freshIdToken) to every call to the APIs in
 * TOKEN_ORIGINS.
 *
 * MSAL's own auto-attach is deliberately off (msal.config.ts keeps an empty protectedResourceMap)
 * so that a request made while signed out passes through unauthenticated instead of firing an
 * interactive External ID redirect from wherever the user happens to be. This does the attaching instead.
 *
 * On a 401 from Klidi.Api the cached merchant is dropped and the user is sent back through sign-in,
 * returning to the page they were on. A 401 from BC Identity or BC Corporate is left to the caller:
 * those back the registration wizard, which handles its own failures.
 */
export const apiTokenInterceptor: HttpInterceptorFn = (req, next) => {
  const msal = inject(MsalService);
  const merchants = inject(MerchantService);
  const auth = inject(AuthService);
  const router = inject(Router);

  if (!TOKEN_ORIGINS.some((origin) => req.url.startsWith(origin))) return next(req);

  const isKlidiApi = req.url.startsWith(environment.apiBaseUrl);

  const handle401 = (err: unknown) => {
    if (!isKlidiApi || !(err instanceof HttpErrorResponse) || err.status !== 401) {
      return throwError(() => err);
    }
    // The gateway refused the key, not the user: signing in again would only bounce straight back here.
    if (isApimKeyRejection(err.error)) {
      console.error('API Management rejected the subscription key — check environment.apim.', err.error);
      return throwError(() => err);
    }

    merchants.clear();
    const url = router.url && !router.url.startsWith('/login-failed') ? router.url : '/dashboard';
    auth.login(url);
    return throwError(() => err);
  };

  const account = msal.instance.getActiveAccount() ?? msal.instance.getAllAccounts()[0] ?? null;
  if (!account) {
    return next(req).pipe(catchError(handle401));
  }

  return from(freshIdToken(msal, account)).pipe(
    switchMap((idToken) =>
      next(req.clone({ setHeaders: { Authorization: `Bearer ${idToken}` } }))
    ),
    catchError(handle401)
  );
};

/** Refresh when the ID token has less than this left, so a request never leaves with one about to expire. */
const ID_TOKEN_MIN_LIFETIME_MS = 5 * 60_000;

/**
 * Klidi.Api accepts the ID token, issued for the Klidi app itself: the app exposes no API scope, so there is no access
 * token for Klidi.Api to receive. MSAL renews on the access token's clock, which can outlive the ID token, so an ID
 * token close to expiry is refreshed explicitly.
 */
async function freshIdToken(msal: MsalService, account: AccountInfo): Promise<string> {
  const request = { scopes: [...environment.apiConfig.klidiApi.scopes], account };
  let result = await msal.instance.acquireTokenSilent(request);

  const exp = (result.idTokenClaims as { exp?: number }).exp ?? 0;
  if (exp * 1000 - Date.now() < ID_TOKEN_MIN_LIFETIME_MS) {
    result = await msal.instance.acquireTokenSilent({ ...request, forceRefresh: true });
  }
  return result.idToken;
}
